import React, { useEffect, useState } from 'react';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Phonebar from '../components/Phonebar';
import WhiteJMJLogo from '../components/WhiteJMJLogo';
import AppointmentCard from '../components/AppointmentCard';
import UnifiedChatbox from '../components/UnifiedChatbox';
import { db } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';

const parseMMDDYYYY = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const parts = dateStr.split('/');
  if (parts.length !== 3) return null;
  const [mm, dd, yyyy] = parts.map((p) => parseInt(p, 10));
  if (!mm || !dd || !yyyy) return null;
  return new Date(yyyy, mm - 1, dd);
};

const toDateSafe = (v) => {
  if (!v) return null;
  if (typeof v?.toDate === 'function') return v.toDate(); // Firestore Timestamp
  if (typeof v === 'string') {
    const mdy = parseMMDDYYYY(v);
    if (mdy) return mdy;
    const d = new Date(v);
    return isNaN(d) ? null : d;
  }
  const d = new Date(v);
  return isNaN(d) ? null : d;
};

const uniq = (arr) => Array.from(new Set(arr.filter(Boolean)));
const dedupeById = (rows) => {
  const m = new Map();
  for (const r of rows) {
    if (r?.id && !m.has(r.id)) m.set(r.id, r);
  }
  return Array.from(m.values());
};

const Home = () => {
  const { client, logout } = useAuth();
  const navigate = useNavigate();
  const [appointment, setAppointment] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleCancelAppointment = () => {
    setAppointment(null);
  };

  const checkAppointment = async () => {
    const phone = client?.phone?.toString();
    const id = client?.id?.toString();
    const keys = uniq([phone, id]);
    if (keys.length === 0) return;

    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    try {
      const appointmentsRef = collection(db, 'appointments');

      // Look up any Scheduled (0) or Rescheduled (4) appointment under either legacy key.
      const snaps = await Promise.all(
        keys.map((k) =>
          getDocs(
            query(
              appointmentsRef,
              where('client', '==', k),
              where('status', 'in', [0, 4])
            )
          )
        )
      );

      const rows = dedupeById(
        snaps.flatMap((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() })))
      );

      const upcoming = rows
        .map((app) => {
          const d = toDateSafe(app.date);
          if (d) d.setHours(0, 0, 0, 0);
          return { ...app, _dateObj: d };
        })
        .filter((app) => app._dateObj && app._dateObj >= todayStart)
        .sort((a, b) => {
          const ad = a._dateObj.getTime();
          const bd = b._dateObj.getTime();
          if (ad !== bd) return ad - bd;
          return (Number(a.startTime) || 0) - (Number(b.startTime) || 0);
        });

      setAppointment(upcoming[0] || null);
    } catch (error) {
      console.error('Error checking appointment:', error);
    }
  };

  useEffect(() => {
    checkAppointment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (client) checkAppointment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  return (
    <div className="min-h-screen bg-white text-[#5e5e5e]">
      <Navbar includeLogout={true} language={client?.language} />
      <Phonebar />
      <h2 className="text-3xl font-bold text-center md:text-left md:flex md:space-x-4 mt-8 md:ml-4 text-JMJGray">
        {client?.language === 'English' ? 'Welcome' : '¡Bienvenida'}, {client?.firstName}!
      </h2>

      <div className="flex flex-col md:flex-row gap-4 items-center md:items-start md:justify-center w-full h-full px-4 py-8 relative">
        {isLoading && (
          <div className="absolute inset-y-0 left-0 w-full flex items-center justify-center z-10 bg-[rgba(0,0,0,0.4)]">
            <div className="flex flex-col items-center gap-2">
              <img src="/hearts.gif" alt="Loading" className="w-40 h-40 object-contain" />
              <div className="text-sm font-medium text-white text-center">
                {client?.language === 'Spanish'
                  ? 'Procesando... ¡no abandone esta página!'
                  : 'Processing... do not exit this page!'}
              </div>
            </div>
          </div>
        )}

        <AppointmentCard
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          appointment={appointment}
          client={client}
          staffView={false}
          onCancel={handleCancelAppointment}
          checkAppointment={checkAppointment}
        />

        <UnifiedChatbox
          fromUser={{
            id: client?.id,
            firstName: client?.firstName || '',
            lastName: client?.lastName || '',
            language: client?.language || 'English',
          }}
          toUser={{
            id: 'JMJ',
            firstName: 'JMJPC',
            lastName: '',
            language: 'English',
          }}
        />
      </div>

      <div className="bg-JMJ flex flex-wrap text-white p-4 gap-6 justify-between">
        <div className="flex flex-col w-full md:w-1/2 lg:w-1/4">
          <WhiteJMJLogo />
          <p>
            {client?.language === 'Spanish'
              ? '@2023 JMJ Pregnancy Center – Todos los derechos reservados.'
              : '@2023 JMJ Pregnancy Center – All Rights Reserved.'}
          </p>
        </div>

        <div className="flex flex-col w-full md:w-1/2 lg:w-1/4">
          <p className="font-bold text-lg">Orlando Clinic</p>
          <p>
            <span className="font-bold">{client?.language === 'Spanish' ? 'Teléfono:' : 'Phone:'}</span>{' '}
            <a href="tel:+14078390620" className="underline">
              (407) 839-0620
            </a>
          </p>
          <p>
            <span className="font-bold">{client?.language === 'Spanish' ? 'Dirección:' : 'Address:'}</span>{' '}
            <a
              href="https://www.google.com/maps/search/?api=1&query=1401+W+Colonial+Orlando+FL+32804"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              1401 W Colonial Dr, Orlando, FL 32804
            </a>
          </p>
          <p>
            <span className="font-bold">{client?.language === 'Spanish' ? 'Horario:' : 'Hours of Operation:'}</span>{' '}
            {client?.language === 'Spanish'
              ? 'Lunes/Miércoles/Viernes 10 AM - 2 PM'
              : 'Monday/Wednesday/Friday 10 AM - 2 PM'}
          </p>
        </div>

        <div className="flex flex-col w-full md:w-1/2 lg:w-1/4">
          <p className="font-bold text-lg">Kissimmee Clinic</p>
          <p>
            <span className="font-bold">{client?.language === 'Spanish' ? 'Teléfono:' : 'Phone:'}</span>{' '}
            <a href="tel:+14072015085" className="underline">
              (407) 201-5085
            </a>
          </p>
          <p>
            <span className="font-bold">{client?.language === 'Spanish' ? 'Dirección:' : 'Address:'}</span>{' '}
            <a
              href="https://www.google.com/maps/search/?api=1&query=800+N+Rose+Ave+Kissimmee+FL+34741"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              800 N Rose Ave, Kissimmee, FL 34741
            </a>
          </p>
          <p>
            <span className="font-bold">{client?.language === 'Spanish' ? 'Horario:' : 'Hours of Operation:'}</span>{' '}
            {client?.language === 'Spanish'
              ? 'Martes/Jueves/Sábado 10 AM - 2 PM'
              : 'Tuesday/Thursday/Saturday 10 AM - 2 PM'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Home;
